import { ITelemetryNamespaceLogger } from '@fluidx/office-fluid-types';
import { ActivityTracker, NamespaceLoggerAdapter } from '@fluidx/utilities';
import { OWHAshaErrorType, OWHAshaPillar, OWHAshaVeto } from './Reliability/OfficeWebHostAsha';
import { logQoSError } from './Reliability/QoSErrorHelpers';
import { Activity, OWHErrorEvent } from './Telemetry';

/**
 * The CreateNewTracker helps FFX team track Reliability and Performance on the CreateNew path.
 * The basic idea is that the host app instantiates the tracker before the createNew scenario starts and calls setSuccess or setFailure to log the result appropriately.
 * The CreateNewTracker accepts a param trackLater, that gives the host app an opportunity to create the tracker early in the lifecycle but only start perf measurement when they want to, by calling the startActivity method on the tracker.
 */

/**
 * It's possible that a user exits a createNew scenario before the file creation/container attach finishes.
 * In such cases FFX has observed that fake fetch failures get logged on the CreateNew path that drop reliability numbers for CreateNew.
 * To solve this problem, we expose a dispose method on the CreateNew tracker, so host apps can call dispose on the tracker if they see user navigating away from the scenario.
 * For sourceless iframe path, host apps also have the option to pass an onDispose callback to the sourcelessIframeLoadOptions that can dispose the createNewTracker on their behalf when the Iframe disposes.
 * This is useful for both avoiding accidental logging and memory cleanups.
 */

/**
 * Additional parameters to the CreateNewTracker
 */
export interface CreateNewTrackerOptions {
  /**
   * If set to true, the constructor will not create the ActivityTracker, rather wait for the caller to call startActivity method on the tracker
   * This is useful when the host apps want to instantiate the tracker early in the lifecycle but want to start perf measurements later
   * An example is on the sourcelessIframe path when the hosts want to create the tracker early on and pass it in the onDispose callback in the sourcelessIframeLoadOptions, for the iframe to dispose the tracker when it unloads,
   * but only start the perf measurement when about to call attach on the container
   */
  trackLater?: boolean;
}

/**
 * A class that tracks create new's perf and reliabiltiy
 */
export class CreateNewTracker {
  private createNewActivityTracker: ActivityTracker | undefined;
  private OWHNamespacedLogger: ITelemetryNamespaceLogger | undefined;
  private readonly trackLater: boolean | undefined;

  constructor(logger: ITelemetryNamespaceLogger, readonly createNewTrackerOptions?: CreateNewTrackerOptions) {
    // Create a namespace logger so the telemetry goes to Office_Fluid_OfficeWebHost_Activity_RealizeNewFile table
    this.OWHNamespacedLogger = new NamespaceLoggerAdapter('OfficeWebHost', logger);
    this.trackLater = createNewTrackerOptions && createNewTrackerOptions.trackLater;
    if (!this.trackLater) {
      this.createNewActivityTracker = ActivityTracker.start(Activity.RealizeNewFile, this.OWHNamespacedLogger);
    }
  }

  /**
   * Exposing an API to start the activity, for callers who do not want to start tracking the activity in the constructor
   * It's possible that host apps want to initialize the CreateNewTracker to allow for passing around much earlier than when it's needed to track perf
   */
  public startActivity() {
    // Making it a no-op by checking trackLater param to avoid accidental calling of startActivity when the activity tracker has already started
    if (this.trackLater) {
      this.createNewActivityTracker = ActivityTracker.start(Activity.RealizeNewFile, this.OWHNamespacedLogger);
    }
  }

  /**
   * Call this method to end the activity and send the success status
   */
  public setSuccess() {
    this.createNewActivityTracker && this.createNewActivityTracker.setResult(true);
  }

  /**
   * Call this method to end the activity and send the failure status
   * @param onAttach Set as true if the failure happens while attaching the container
   * @param error Error object thrown
   */
  public setFailure(onAttach: boolean, error?: any) {
    // Log a QoS Error if container attach or getAbsoluteUrl fail
    this.OWHNamespacedLogger &&
      logQoSError(
        this.OWHNamespacedLogger,
        OWHErrorEvent.CreateNew /*eventName*/,
        OWHAshaPillar.Create /*pillar*/,
        OWHAshaVeto.FileCreateFailed /*veto*/,
        error.errorType ||
          (onAttach
            ? OWHAshaErrorType.ContainerAttachFailed
            : OWHAshaErrorType.GetAbsoluteUrlFailed) /*Asha ErrorType*/,
        undefined /*additionalProps */,
        error
      );
    this.createNewActivityTracker &&
      this.createNewActivityTracker.setResult(false, { errorType: error.errorType } /*additionalProps*/, error);
  }

  /**
   * Method to set the logger to undefined so no further logging can be done from the tracker and disposes the underlying ActivityTracker
   * This is required for memory cleanup and to make sure no accidental logging happens when user navigates away from the CreateNew experience
   */
  public dispose() {
    this.OWHNamespacedLogger = undefined;
    this.createNewActivityTracker?.dispose();
  }
}
